import { useState, useCallback, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { BAKERY_RECIPES } from '../../data/bakeryRecipes'
import type { Recipe, RecipeStep } from '../../types'
import { playScanSound, playCheckoutSound } from '../../services/sounds'
import { hapticLight, hapticSuccess } from '../../utils/haptic'

function getRecipe(id: string): Recipe | null {
  return BAKERY_RECIPES.find((r) => r.id === id) ?? null
}

function BuildVisual({ completedSteps, currentStage, nextEmoji }: { completedSteps: RecipeStep[]; currentStage?: string; nextEmoji?: string }) {
  const emojis = completedSteps.map((s) => s.emoji).filter(Boolean)
  const stage = currentStage ?? 'bowl'
  const isMix = stage === 'mix'
  const isOven = stage === 'oven'
  const isDecorate = stage === 'decorate'
  return (
    <div className="bake-build" aria-hidden>
      {nextEmoji ? (
        <div className="bake-build-next" aria-hidden>
          <span className="bake-build-next-label">Add this</span>
          <span className="bake-build-next-emoji">{nextEmoji}</span>
        </div>
      ) : null}
      <div className={`bake-build-scene ${isOven ? 'bake-build-scene--oven' : ''}`}>
        {isOven && <div className="bake-oven-glow" aria-hidden />}
        {isMix && <span className="bake-spoon" aria-hidden>🥄</span>}
        <div className="bake-build-bowl" data-stage={stage}>
          {emojis.length > 0 ? (
            <span className={`bake-build-ingredients ${isMix ? 'bake-build-ingredients--mixing' : ''} ${isDecorate ? 'bake-build-ingredients--decorate' : ''}`}>
              {emojis.map((e, i) => (
                <span key={i} className="bake-build-emoji" style={{ animationDelay: `${i * 0.05}s` }}>{e}</span>
              ))}
            </span>
          ) : (
            <span className="bake-build-empty">🥣</span>
          )}
        </div>
      </div>
    </div>
  )
}

export function BakeRecipeView() {
  const { recipeId } = useParams<{ recipeId: string }>()
  const recipe = recipeId ? getRecipe(recipeId) : null
  const [stepIndex, setStepIndex] = useState(0)

  const handleNext = useCallback(() => {
    if (!recipe) return
    hapticLight()
    playScanSound()
    if (stepIndex < recipe.steps.length - 1) {
      setStepIndex((i) => i + 1)
    } else {
      setStepIndex((i) => i + 1)
    }
  }, [recipe, stepIndex])

  if (!recipe) {
    return (
      <div className="page page--bake">
        <div className="empty-state">
          <span className="empty-state-emoji" aria-hidden>🧁</span>
          <h2 className="empty-state-title">Recipe not found</h2>
          <Link to="/bakery/bake" className="btn-large btn-primary" style={{ textDecoration: 'none' }}>
            Pick a recipe
          </Link>
        </div>
      </div>
    )
  }

  const steps = recipe.steps
  const currentStep = steps[stepIndex]
  const completedSteps = steps.slice(0, stepIndex)
  const isLastStep = stepIndex === steps.length - 1
  const isFinished = stepIndex >= steps.length
  const currentStage = currentStep?.stage

  useEffect(() => {
    if (isFinished && steps.length > 0) {
      hapticSuccess()
      playCheckoutSound()
      import('canvas-confetti')
        .then((mod) => {
          try {
            mod.default({ particleCount: 100, spread: 70, origin: { y: 0.7 } })
            setTimeout(() => {
              mod.default({ particleCount: 50, spread: 60, origin: { x: 0.2, y: 0.8 } })
              mod.default({ particleCount: 50, spread: 60, origin: { x: 0.8, y: 0.8 } })
            }, 200)
          } catch {
            // ignore
          }
        })
        .catch(() => {})
    }
  }, [isFinished, steps.length])

  if (isFinished && steps.length > 0) {
    return (
      <div className="page page--bake page--bake-done">
        <div className="bake-done-content">
          <div className="bake-done-treat" aria-hidden>
            <span className="bake-done-treat-emoji">{recipe.emoji}</span>
          </div>
          <h1 className="bake-done-title">You did it! 🎉</h1>
          <p className="bake-done-name">
            <span className="bake-done-emoji" aria-hidden>{recipe.emoji}</span>
            {recipe.name}
          </p>
          <p className="bake-done-message">You made it in the app – and you can make it in real life too!</p>
        </div>
        <div className="bake-done-actions">
          <Link to={`/bakery/bake/${recipe.id}`} className="btn-large btn-primary" style={{ textDecoration: 'none' }}>
            Bake again
          </Link>
          <Link to="/bakery/bake" className="btn-large btn-secondary" style={{ textDecoration: 'none' }}>
            Pick another recipe
          </Link>
          <Link to="/bakery" className="btn-large btn-secondary" style={{ textDecoration: 'none' }}>
            Back to Bakery
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="page page--bake">
      <header className="bake-header">
        <Link to="/bakery/bake" className="btn-back" aria-label="Back to recipes">←</Link>
        <h1 className="bake-recipe-name">
          <span aria-hidden>{recipe.emoji}</span> {recipe.name}
        </h1>
        <div className="bake-progress-wrap" aria-live="polite">
          <span className="bake-progress-text">Step {stepIndex + 1} of {steps.length}</span>
          <div className="bake-progress-dots" role="presentation">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`bake-progress-dot ${i < stepIndex ? 'bake-progress-dot--done' : ''} ${i === stepIndex ? 'bake-progress-dot--current' : ''}`}
                aria-hidden
              />
            ))}
          </div>
        </div>
      </header>

      <BuildVisual completedSteps={completedSteps} currentStage={currentStage} nextEmoji={currentStep?.emoji} />

      <section className="bake-step" aria-labelledby="bake-step-label">
        <h2 id="bake-step-label" className="bake-step-label">{currentStep?.label}</h2>
        {currentStep?.instruction && (
          <p className="bake-step-instruction">{currentStep.instruction}</p>
        )}
        <button
          type="button"
          className="btn-large btn-primary bake-step-done"
          onClick={handleNext}
          aria-label={isLastStep ? 'Finish' : 'I did it! Next step'}
        >
          <span className="bake-step-done-icon" aria-hidden>{isLastStep ? '✨' : '👍'}</span>
          {isLastStep ? "I'm done!" : "I did it!"}
        </button>
      </section>
    </div>
  )
}
